import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  Menu,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Alert,
} from "@mui/material";

import {
  addContainerLabelFilterValue,
  removeContainerLabelFilterValue,
  addContainerTypeFilterValue,
  removeContainerTypeFilterValue,
  clearContainerLabelAndTypeFilters,
} from "../../data/filter";

export interface FilterMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  width?: number | string;
  labels: { id: string; name: string; color: string }[];
  types: { id: string; name: string }[];
  selectedLabels: string[];
  selectedTypes: string[];
}

const FilterMenu: React.FC<FilterMenuProps> = ({
  anchorEl,
  open,
  onClose,
  width = 400,
  labels,
  types,
  selectedLabels,
  selectedTypes,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [menuLabels, setMenuLabels] = useState<string[]>([]);
  const [menuTypes, setMenuTypes] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setMenuLabels(selectedLabels);
      setMenuTypes(selectedTypes);
    }
  }, [open, selectedLabels, selectedTypes]);

  const toggleLabel = (id: string) => {
    const isActive = menuLabels.includes(id);
    const updated = isActive
      ? menuLabels.filter(l => l !== id)
      : [...menuLabels, id];
    setMenuLabels(updated);

    if (isActive) {
      dispatch(removeContainerLabelFilterValue(id));
    } else {
      dispatch(addContainerLabelFilterValue(id));
    }
  };

  const toggleType = (id: string) => {
    const isActive = menuTypes.includes(id);
    const updated = isActive
      ? menuTypes.filter(t => t !== id)
      : [...menuTypes, id];
    setMenuTypes(updated);

    if (isActive) {
      dispatch(removeContainerTypeFilterValue(id));
    } else {
      dispatch(addContainerTypeFilterValue(id));
    }
  };

  const handleClear = () => {
    dispatch(clearContainerLabelAndTypeFilters());
    setMenuLabels([]);
    setMenuTypes([]);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width, p: 1 } }}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Box display="flex" flexWrap="wrap" gap={2}>
        <Box width="calc(50% - 8px)">
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t("labels")}
          </Typography>
          {labels.length === 0 ? (
            <Alert severity="info">{ t("noDataTypeAvailable", { type: t("labels") }) }</Alert>
          ) : (
            <List dense disablePadding>
              {labels.map(l => (
                <ListItem key={l.id} button onClick={() => toggleLabel(l.id)}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      size="small"
                      checked={menuLabels.includes(l.id)}
                      sx={{ color: l.color }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={l.name}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Box width="calc(50% - 8px)">
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t("types")}
          </Typography>
          {types.length === 0 ? (
            <Alert severity="info">{ t("noDataTypeAvailable", { type: t("type") }) }</Alert>
          ) : (
            <List dense disablePadding>
              {types.map(t => (
                <ListItem key={t.id} button onClick={() => toggleType(t.id)}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Checkbox
                      size="small"
                      checked={menuTypes.includes(t.id)}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={t.name}
                    primaryTypographyProps={{ noWrap: true }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        mt={1}
        borderTop="1px solid #DDD"
        pt={1}
      >
        <Button size="small" onClick={handleClear}>
          {t("clear")}
        </Button>
        <Button size="small" onClick={onClose}>
          {t("close")}
        </Button>
      </Box>
    </Menu>
  );
};

export default FilterMenu;